import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppWindow, ChevronDown, ChevronUp, Copy, Eye, EyeOff, FlipHorizontal, FlipVertical, Maximize, Maximize2, Minimize,
  MoveHorizontal, Redo2, RotateCw, Scissors, Trash2, Undo2,
} from 'lucide-react';
import type { PrintCrop, PrintPlacement, PrintTemplate, PrintView } from '@wrapforce/shared';
import { PRINT_VIEWS } from '@wrapforce/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Select, SelectItem } from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { PrintCanvas, type PrintCanvasHandle } from './PrintCanvas';
import { usePlacementHistory } from './usePlacementHistory';
import {
  centerLayerInView, clampCrop, createLayerForArtwork, fitLayerToPanel, fitLayerToView, isTypingTarget,
  mirrorLayersToPassenger, panelsForView, PRINT_VIEW_LABEL_KEYS, readShowWindowsPreference, rotate90, writeShowWindowsPreference,
} from './helpers';

export interface EditorArtwork {
  id: string;
  objectUrl: string | null;
  width: number | null;
  height: number | null;
}

// Photoshop-style arrow-key nudge amounts (in, Shift for the larger step).
const NUDGE_IN = 0.25;
const NUDGE_IN_SHIFT = 2;

export function PlacementEditor({
  template, artworks, placements, resetKey, outlineUrls, onChange,
}: {
  template: PrintTemplate;
  artworks: EditorArtwork[];
  placements: PrintPlacement[];
  resetKey: string;
  // AI-drawn outline layer images, keyed by media id - see PrintCanvas.
  outlineUrls?: Record<string, string>;
  onChange: (next: PrintPlacement[]) => void;
}) {
  const { t } = useTranslation('printWraps');

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Stable identity so the history hook doesn't recreate its callbacks every
  // render just because the parent passed a fresh onChange closure.
  const forwardCommit = useRef((next: PrintPlacement[]) => onChangeRef.current(next)).current;
  const history = usePlacementHistory(placements, forwardCommit);

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // CSS-overlay fallback for when the Fullscreen API is unavailable or
  // rejects (e.g. iOS Safari).
  const [fullscreenFallback, setFullscreenFallback] = useState(false);
  const fullscreenActive = isFullscreen || fullscreenFallback;

  const [previewMode, setPreviewMode] = useState(false);
  const [showWindows, setShowWindows] = useState(() => readShowWindowsPreference());
  const [activeView, setActiveView] = useState<PrintView>('driver_side');
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [cropMode, setCropMode] = useState(false);
  const [cropDraft, setCropDraft] = useState<PrintCrop | null>(null);
  const [lockAspect, setLockAspect] = useState(false);
  const resetKeyRef = useRef(resetKey);
  const canvasRef = useRef<PrintCanvasHandle>(null);

  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    history.reset(placements);
    setSelectedLayerId(null);
    setCropMode(false);
    setCropDraft(null);
    // Only re-seed when resetKey (the design id) changes - after mount the
    // editor owns live state + history, so `placements`/`history` are
    // intentionally excluded from the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    function onFullscreenChange() {
      const native = document.fullscreenElement === rootEl;
      setIsFullscreen(native);
      if (native) setFullscreenFallback(false);
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [rootEl]);

  const artworksById = useMemo(() => {
    const map: Record<string, EditorArtwork> = {};
    for (const a of artworks) map[a.id] = a;
    return map;
  }, [artworks]);

  const layersForView = useMemo(() => history.present.filter((p) => p.view === activeView), [history.present, activeView]);
  const selectedLayer = history.present.find((p) => p.id === selectedLayerId) ?? null;
  const viewDims = template.views[activeView];
  const widthIn = viewDims?.widthIn ?? template.overall.lengthIn;
  const heightIn = viewDims?.heightIn ?? template.overall.heightIn;
  const panels = useMemo(() => panelsForView(template, activeView), [template, activeView]);

  function selectView(view: PrintView) {
    setActiveView(view);
    setSelectedLayerId(null);
    setCropMode(false);
    setCropDraft(null);
  }

  function updateSelected(mutate: (layer: PrintPlacement) => PrintPlacement) {
    if (!selectedLayer) return;
    history.commit(history.present.map((p) => (p.id === selectedLayer.id ? mutate(p) : p)));
  }

  function handleAddArtwork(artworkId: string) {
    const artwork = artworksById[artworkId];
    const aspect = artwork?.width && artwork.height ? artwork.width / artwork.height : null;
    const layer = createLayerForArtwork({
      view: activeView, artworkMediaId: artworkId, artworkAspect: aspect, viewWidthIn: widthIn, viewHeightIn: heightIn,
    });
    history.commit([...history.present, layer]);
    setSelectedLayerId(layer.id);
    setCropMode(false);
  }

  // The layer-row buttons pass their own layer id: selecting it and then
  // reading selectedLayer in the same click would still see the previously
  // selected layer, since the state update hasn't rendered yet.
  function handleDeleteLayer(layerId: string) {
    history.commit(history.present.filter((p) => p.id !== layerId));
    if (layerId === selectedLayerId) {
      setSelectedLayerId(null);
      setCropMode(false);
      setCropDraft(null);
    }
  }

  function handleDuplicateLayer(layerId: string) {
    const source = history.present.find((p) => p.id === layerId);
    if (!source) return;
    const copy: PrintPlacement = { ...source, id: crypto.randomUUID().slice(0, 12), x: source.x + 1, y: source.y + 1 };
    history.commit([...history.present, copy]);
    setSelectedLayerId(copy.id);
  }

  function handleDeleteSelected() {
    if (selectedLayer) handleDeleteLayer(selectedLayer.id);
  }

  function handleDuplicateSelected() {
    if (selectedLayer) handleDuplicateLayer(selectedLayer.id);
  }

  function moveLayer(id: string, dir: -1 | 1) {
    const layers = [...history.present];
    const viewIds = layers.filter((p) => p.view === activeView).map((p) => p.id);
    const idx = viewIds.indexOf(id);
    const swapWith = idx + dir;
    if (idx < 0 || swapWith < 0 || swapWith >= viewIds.length) return;
    const globalA = layers.findIndex((p) => p.id === viewIds[idx]);
    const globalB = layers.findIndex((p) => p.id === viewIds[swapWith]);
    const tmp = layers[globalA]!;
    layers[globalA] = layers[globalB]!;
    layers[globalB] = tmp;
    history.commit(layers);
  }

  function handleMirrorToPassenger() {
    const driverLayers = history.present.filter((p) => p.view === 'driver_side');
    const driverDims = template.views.driver_side;
    if (driverLayers.length === 0 || !driverDims) return;
    const mirrored = mirrorLayersToPassenger(driverLayers, driverDims.widthIn);
    const withoutOldPassenger = history.present.filter((p) => p.view !== 'passenger_side');
    history.commit([...withoutOldPassenger, ...mirrored]);
  }

  function toggleCropMode() {
    if (!selectedLayer) return;
    if (cropMode) {
      if (cropDraft) updateSelected((l) => ({ ...l, crop: clampCrop(cropDraft) }));
      setCropMode(false);
      setCropDraft(null);
    } else {
      setCropDraft(selectedLayer.crop);
      setCropMode(true);
    }
  }

  function cancelCropMode() {
    setCropMode(false);
    setCropDraft(null);
  }

  function togglePreview() {
    setPreviewMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedLayerId(null);
        setCropMode(false);
        setCropDraft(null);
      }
      return next;
    });
  }

  function toggleShowWindows() {
    setShowWindows((prev) => {
      const next = !prev;
      writeShowWindowsPreference(next);
      return next;
    });
  }

  async function toggleFullscreen() {
    if (isFullscreen) {
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen();
        } catch {
          // ignore - nothing more we can do
        }
      }
      return;
    }
    if (fullscreenFallback) {
      setFullscreenFallback(false);
      return;
    }
    if (rootEl?.requestFullscreen) {
      try {
        // Some embedded browsers neither grant nor reject the request, so
        // treat "not fullscreen after a second" the same as a rejection.
        await Promise.race([
          rootEl.requestFullscreen(),
          new Promise<void>((resolve) => setTimeout(resolve, 1000)),
        ]);
        if (document.fullscreenElement === rootEl) return;
      } catch {
        // fall through to the CSS overlay fallback (e.g. iOS Safari, or a
        // permissions policy that blocks the Fullscreen API)
      }
    }
    setFullscreenFallback(true);
  }

  // A ref-backed snapshot of everything the keydown listener needs, updated
  // every render - the listener itself is attached once with an empty dep
  // array, so it always reads the latest state/handlers without needing to
  // be torn down and re-attached on every keystroke-relevant change.
  const latestRef = useRef({
    selectedLayer, selectedLayerId, cropMode, fullscreenFallback,
    undo: history.undo, redo: history.redo,
    duplicateSelected: handleDuplicateSelected, deleteSelected: handleDeleteSelected,
    updateSelected, cancelCropMode, togglePreview, toggleShowWindows,
    setSelectedLayerId, setFullscreenFallback,
  });
  latestRef.current = {
    selectedLayer, selectedLayerId, cropMode, fullscreenFallback,
    undo: history.undo, redo: history.redo,
    duplicateSelected: handleDuplicateSelected, deleteSelected: handleDeleteSelected,
    updateSelected, cancelCropMode, togglePreview, toggleShowWindows,
    setSelectedLayerId, setFullscreenFallback,
  };

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const s = latestRef.current;
      if (isTypingTarget(e.target)) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'z' || e.key === 'Z') && e.shiftKey) {
        e.preventDefault();
        s.redo();
        return;
      }
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        s.undo();
        return;
      }
      if (mod && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        s.redo();
        return;
      }
      if (mod && (e.key === 'd' || e.key === 'D' || e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        s.duplicateSelected();
        return;
      }
      if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        canvasRef.current?.zoomIn();
        return;
      }
      if (mod && e.key === '-') {
        e.preventDefault();
        canvasRef.current?.zoomOut();
        return;
      }
      if (mod && e.key === '0') {
        e.preventDefault();
        canvasRef.current?.zoomToFit();
        return;
      }
      if (mod && e.key === '1') {
        e.preventDefault();
        canvasRef.current?.zoomTo100();
        return;
      }
      if (!mod && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        canvasRef.current?.setTool('move');
        return;
      }
      if (!mod && (e.key === 'h' || e.key === 'H')) {
        e.preventDefault();
        canvasRef.current?.setTool('hand');
        return;
      }
      if (!mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        canvasRef.current?.setTool('zoom');
        return;
      }
      if (!mod && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        s.togglePreview();
        return;
      }
      if (!mod && (e.key === 'w' || e.key === 'W')) {
        e.preventDefault();
        s.toggleShowWindows();
        return;
      }
      if (e.key === 'Escape') {
        if (s.cropMode) {
          s.cancelCropMode();
        } else if (s.selectedLayerId) {
          s.setSelectedLayerId(null);
        } else if (s.fullscreenFallback) {
          s.setFullscreenFallback(false);
        }
        return;
      }
      if (!s.selectedLayer) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        s.deleteSelected();
        return;
      }
      const nudge = e.shiftKey ? NUDGE_IN_SHIFT : NUDGE_IN;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        s.updateSelected((l) => ({ ...l, x: l.x - nudge }));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        s.updateSelected((l) => ({ ...l, x: l.x + nudge }));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        s.updateSelected((l) => ({ ...l, y: l.y - nudge }));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        s.updateSelected((l) => ({ ...l, y: l.y + nudge }));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      ref={setRootEl}
      className={cn('flex flex-col gap-3', fullscreenActive && 'fixed inset-0 z-50 overflow-hidden bg-surface p-4')}
    >
      <div className="flex flex-wrap gap-1.5 border-b border-border pb-2">
        {PRINT_VIEWS.map((view) => {
          const count = history.present.filter((p) => p.view === view).length;
          return (
            <button
              key={view}
              type="button"
              onClick={() => selectView(view)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide',
                view === activeView ? 'bg-ink text-surface' : 'bg-surface-2 text-muted hover:text-ink',
              )}
            >
              {t(PRINT_VIEW_LABEL_KEYS[view])}
              {count > 0 ? <span className="opacity-70">({count})</span> : null}
            </button>
          );
        })}
      </div>

      <div className={cn('grid gap-3', fullscreenActive ? 'min-h-0 flex-1 grid-cols-[220px_1fr]' : 'grid-cols-1 lg:grid-cols-[220px_1fr]')}>
        <div className={cn('flex flex-col gap-2 border border-border p-3', fullscreenActive && 'overflow-y-auto')}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-muted">{t('editor.layers.title')}</span>
            <Select
              value=""
              onValueChange={handleAddArtwork}
              disabled={artworks.length === 0 || previewMode}
              placeholder={t('editor.layers.addArtwork')}
              ariaLabel={t('editor.layers.addArtwork')}
              portalContainer={rootEl}
            >
              {artworks.map((a, idx) => (
                <SelectItem key={a.id} value={a.id}>{t('editor.layers.artworkOption', { n: idx + 1 })}</SelectItem>
              ))}
            </Select>
          </div>
          {layersForView.length === 0 ? (
            <p className="text-xs text-muted">{t('editor.layers.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {[...layersForView].reverse().map((layer, reverseIdx) => {
                const n = layersForView.length - reverseIdx;
                const artwork = layer.artworkMediaId ? artworksById[layer.artworkMediaId] : undefined;
                const selected = layer.id === selectedLayerId;
                return (
                  <li
                    key={layer.id}
                    className={cn('flex items-center gap-2 border px-2 py-1.5 text-xs', selected ? 'border-accent bg-surface-2' : 'border-border')}
                  >
                    <button
                      type="button"
                      disabled={previewMode}
                      className="flex flex-1 items-center gap-2 text-left disabled:opacity-60"
                      onClick={() => { setSelectedLayerId(layer.id); setCropMode(false); }}
                    >
                      {artwork?.objectUrl ? (
                        <img src={artwork.objectUrl} alt="" className="h-7 w-7 border border-border object-cover" />
                      ) : (
                        <span className="h-7 w-7 border border-border bg-surface-2" />
                      )}
                      <span className="truncate">{t('editor.layers.layerLabel', { n })}</span>
                    </button>
                    <button type="button" disabled={previewMode} aria-label={t('editor.layers.moveUp')} title={t('editor.layers.moveUp')} onClick={() => moveLayer(layer.id, 1)} className="text-muted hover:text-ink disabled:opacity-60">
                      <ChevronUp size={13} />
                    </button>
                    <button type="button" disabled={previewMode} aria-label={t('editor.layers.moveDown')} title={t('editor.layers.moveDown')} onClick={() => moveLayer(layer.id, -1)} className="text-muted hover:text-ink disabled:opacity-60">
                      <ChevronDown size={13} />
                    </button>
                    <button
                      type="button"
                      disabled={previewMode}
                      aria-label={t('editor.layers.duplicate')}
                      title={t('editor.layers.duplicate')}
                      onClick={() => handleDuplicateLayer(layer.id)}
                      className="text-muted hover:text-ink disabled:opacity-60"
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      type="button"
                      disabled={previewMode}
                      aria-label={t('editor.layers.delete')}
                      title={t('editor.layers.delete')}
                      onClick={() => handleDeleteLayer(layer.id)}
                      className="text-muted hover:text-danger disabled:opacity-60"
                    >
                      <Trash2 size={13} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className={cn('flex flex-col gap-2', fullscreenActive && 'min-h-0')}>
          <div className="flex flex-wrap items-center gap-1.5">
            <Button type="button" size="sm" variant="outline" disabled={!history.canUndo} onClick={() => history.undo()} aria-label={t('editor.toolbar.undo')} title={t('editor.toolbar.undo')}>
              <Undo2 size={14} />
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={!history.canRedo} onClick={() => history.redo()} aria-label={t('editor.toolbar.redo')} title={t('editor.toolbar.redo')}>
              <Redo2 size={14} />
            </Button>
            <div className="mx-1 h-5 w-px bg-border" />
            <Button type="button" size="sm" variant={cropMode ? 'primary' : 'outline'} disabled={!selectedLayer || previewMode} onClick={toggleCropMode}>
              <Scissors size={13} className="mr-1" /> {cropMode ? t('editor.toolbar.cropDone') : t('editor.toolbar.crop')}
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={!selectedLayer || cropMode || previewMode} onClick={() => updateSelected((l) => ({ ...l, flipX: !l.flipX }))} aria-label={t('editor.toolbar.flipHorizontal')} title={t('editor.toolbar.flipHorizontal')}>
              <FlipHorizontal size={14} />
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={!selectedLayer || cropMode || previewMode} onClick={() => updateSelected((l) => ({ ...l, flipY: !l.flipY }))} aria-label={t('editor.toolbar.flipVertical')} title={t('editor.toolbar.flipVertical')}>
              <FlipVertical size={14} />
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={!selectedLayer || cropMode || previewMode} onClick={() => updateSelected((l) => ({ ...l, rotationDeg: rotate90(l.rotationDeg) }))} aria-label={t('editor.toolbar.rotate90')} title={t('editor.toolbar.rotate90')}>
              <RotateCw size={14} />
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={!selectedLayer || cropMode || previewMode} onClick={() => updateSelected((l) => fitLayerToView(l, widthIn, heightIn))}>
              <Maximize2 size={13} className="mr-1" /> {t('editor.toolbar.fitToView')}
            </Button>
            {panels.length > 0 ? (
              <Select
                value=""
                placeholder={t('editor.toolbar.fitToPanel')}
                ariaLabel={t('editor.toolbar.fitToPanel')}
                disabled={!selectedLayer || cropMode || previewMode}
                portalContainer={rootEl}
                onValueChange={(panelKey) => {
                  const panel = panels.find((p) => p.key === panelKey);
                  if (panel) updateSelected((l) => fitLayerToPanel(l, panel));
                }}
              >
                {panels.map((panel) => (
                  <SelectItem key={`${panel.view}:${panel.key}`} value={panel.key}>{panel.label}</SelectItem>
                ))}
              </Select>
            ) : null}
            <Button type="button" size="sm" variant="outline" disabled={!selectedLayer || cropMode || previewMode} onClick={() => updateSelected((l) => centerLayerInView(l, widthIn, heightIn))}>
              {t('editor.toolbar.center')}
            </Button>
            {activeView === 'driver_side' ? (
              <Button type="button" size="sm" variant="outline" disabled={layersForView.length === 0 || previewMode} onClick={handleMirrorToPassenger}>
                <MoveHorizontal size={13} className="mr-1" /> {t('editor.toolbar.copyToPassenger')}
              </Button>
            ) : null}
            <div className="mx-1 h-5 w-px bg-border" />
            <Button
              type="button"
              size="sm"
              variant={showWindows ? 'primary' : 'outline'}
              onClick={toggleShowWindows}
              aria-pressed={showWindows}
              aria-label={showWindows ? t('editor.toolbar.hideWindows') : t('editor.toolbar.showWindows')}
              title={showWindows ? t('editor.toolbar.hideWindows') : t('editor.toolbar.showWindows')}
            >
              <AppWindow size={14} />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={previewMode ? 'primary' : 'outline'}
              onClick={togglePreview}
              aria-label={previewMode ? t('editor.toolbar.exitPreview') : t('editor.toolbar.enterPreview')}
              title={previewMode ? t('editor.toolbar.exitPreview') : t('editor.toolbar.enterPreview')}
            >
              {previewMode ? <EyeOff size={14} /> : <Eye size={14} />}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void toggleFullscreen()}
              aria-label={fullscreenActive ? t('editor.toolbar.exitFullscreen') : t('editor.toolbar.fullscreen')}
              title={fullscreenActive ? t('editor.toolbar.exitFullscreen') : t('editor.toolbar.fullscreen')}
            >
              {fullscreenActive ? <Minimize size={14} /> : <Maximize size={14} />}
            </Button>
          </div>

          <div className={cn(fullscreenActive && 'min-h-0 flex-1')}>
            <PrintCanvas
              ref={canvasRef}
              template={template}
              view={activeView}
              layers={layersForView}
              artworks={artworksById}
              selectedLayerId={selectedLayerId}
              cropMode={cropMode}
              cropDraft={cropDraft}
              fillContainer={fullscreenActive}
              previewMode={previewMode}
              showWindows={showWindows}
              outlineUrls={outlineUrls}
              onSelectLayer={(id) => { setSelectedLayerId(id); if (!id) { setCropMode(false); setCropDraft(null); } }}
              onGestureStart={() => history.beginGesture()}
              onGestureChange={(id, next) => history.updateLive(history.present.map((p) => (p.id === id ? next : p)))}
              onGestureEnd={(id, next) => history.commitGesture(history.present.map((p) => (p.id === id ? next : p)))}
              onCropDraftChange={setCropDraft}
            />
          </div>

          {previewMode ? null : layersForView.length === 0 ? (
            <p className="text-xs text-muted">{t('editor.noLayersHint')}</p>
          ) : !selectedLayer ? (
            <p className="text-xs text-muted">{t('editor.noSelectionHint')}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 border border-border p-3 sm:grid-cols-5">
              <NumberField label={t('editor.inspector.x')} value={selectedLayer.x} onCommit={(v) => updateSelected((l) => ({ ...l, x: v }))} />
              <NumberField label={t('editor.inspector.y')} value={selectedLayer.y} onCommit={(v) => updateSelected((l) => ({ ...l, y: v }))} />
              <NumberField
                label={t('editor.inspector.w')}
                value={selectedLayer.w}
                onCommit={(v) => updateSelected((l) => (lockAspect ? { ...l, w: v, h: (v * l.h) / l.w } : { ...l, w: v }))}
              />
              <NumberField
                label={t('editor.inspector.h')}
                value={selectedLayer.h}
                onCommit={(v) => updateSelected((l) => (lockAspect ? { ...l, h: v, w: (v * l.w) / l.h } : { ...l, h: v }))}
              />
              <NumberField label={t('editor.inspector.rotation')} value={selectedLayer.rotationDeg} step={1} onCommit={(v) => updateSelected((l) => ({ ...l, rotationDeg: Math.max(-180, Math.min(180, v)) }))} />
              <label className="col-span-2 flex items-center gap-2 text-xs text-muted sm:col-span-5">
                <input type="checkbox" checked={lockAspect} onChange={(e) => setLockAspect(e.target.checked)} />
                {t('editor.inspector.lockAspect')}
              </label>
            </div>
          )}

          {previewMode ? (
            <p className="text-xs text-muted">{t('editor.preview.note')}</p>
          ) : (
            <div className="flex flex-wrap gap-3 text-xs text-muted">
              <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 border border-ink/40" style={{ background: 'rgba(37,99,235,0.25)' }} />{t('editor.legend.printed')}</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 border border-ink/40 opacity-40" />{t('editor.legend.trimmed')}</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 border border-[#93C5FD]" style={{ background: '#DBEAFE' }} />{t('editor.legend.glass')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onCommit, step = 0.1 }: { label: string; value: number; onCommit: (v: number) => void; step?: number }) {
  const [draft, setDraft] = useState(value.toFixed(1));
  useEffect(() => setDraft(value.toFixed(1)), [value]);

  function commit() {
    const parsed = Number(draft);
    if (Number.isFinite(parsed)) onCommit(parsed);
    else setDraft(value.toFixed(1));
  }

  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input
        type="number"
        step={step}
        className="h-8"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
    </div>
  );
}
